import type { GlobalAssistantSuggestMatch } from './global-ai-assistant-api';
import type { GlobalAiSingleChartTranscriptMessage } from './global-ai-assistant-transcript';

const STANDARD_INDIVIDUAL_DISCOVERY_PAGE_ID = '__std__:app:talent-discovery';
const STANDARD_INDIVIDUAL_DISCOVERY_NAME = 'Individual Discovery';
const STANDARD_PAGE_LABEL_BY_SEGMENT: Record<string, string> = {
  summary: 'Overview',
  compositions: 'Compositions',
  transitions: 'Transitions',
  postings: 'Job Postings',
  pay: 'Pay',
  sentiment: 'Sentiment',
  screener: 'Discovery',
};
const STANDARD_ENTITY_LABEL_BY_SEGMENT: Record<string, string> = {
  company: 'Company',
  geography: 'Geography',
  role: 'Role',
};

function toStandardPageLabel(standardPagePath: string): string {
  const segments = standardPagePath.split('/').filter(Boolean);
  const firstSegment = segments[0];
  const secondSegment = segments[1];

  if (firstSegment === 'talent-discovery') {
    return STANDARD_INDIVIDUAL_DISCOVERY_NAME;
  }
  if (
    !firstSegment ||
    !secondSegment ||
    !(firstSegment in STANDARD_ENTITY_LABEL_BY_SEGMENT)
  ) {
    return 'standard page';
  }
  const entityLabel = STANDARD_ENTITY_LABEL_BY_SEGMENT[firstSegment];
  const pageLabel = STANDARD_PAGE_LABEL_BY_SEGMENT[secondSegment];
  if (!entityLabel || !pageLabel) {
    return 'standard page';
  }
  return `${entityLabel} ${pageLabel}`;
}

function toStandardPageId(standardPagePath: string): string {
  const segments = standardPagePath.split('/').filter(Boolean);
  const firstSegment = segments[0];
  const secondSegment = segments[1];
  if (firstSegment === 'talent-discovery') {
    return STANDARD_INDIVIDUAL_DISCOVERY_PAGE_ID;
  }
  if (!firstSegment || !secondSegment) {
    return '__std__:custom:standard-page';
  }
  return `__std__:${firstSegment}:${secondSegment}`;
}

function isIndividualDiscoveryPath(standardPagePath: string): boolean {
  return standardPagePath.split('/').filter(Boolean)[0] === 'talent-discovery';
}

export function getSingleChartStandardPageButtonLabel(
  standardPagePath: string | undefined
): string {
  const normalizedPath = standardPagePath?.trim();
  if (!normalizedPath) {
    return 'Open in standard page';
  }
  return `Open in ${toStandardPageLabel(normalizedPath)}`;
}

export function getSingleChartStandardPageMatch(
  msg: GlobalAiSingleChartTranscriptMessage
): GlobalAssistantSuggestMatch | null {
  const standardPagePath = msg.standardPagePath?.trim();
  if (!standardPagePath) {
    return null;
  }
  const isIndividualDiscovery = isIndividualDiscoveryPath(standardPagePath);
  const maybeResolvedPrompt = msg.prompt?.trim();
  return {
    pageId: toStandardPageId(standardPagePath),
    name: toStandardPageLabel(standardPagePath),
    navigatePath: standardPagePath,
    confidenceScore: 100,
    ...(isIndividualDiscovery
      ? {
          discoveryKind: 'talent' as const,
          applyFiltersAfterNavigate: true,
        }
      : {}),
    ...(maybeResolvedPrompt
      ? { resolvedPromptForApply: maybeResolvedPrompt }
      : {}),
  };
}
